/*
 * Copyright (c) 2026 Besnovatyj. Licensed under the MIT License.
 */

/**
 * StarRain Theme — главный JS entry point
 *
 * Template Name: Gp - v4.7.0
 * Template URL: https://bootstrapmade.com/gp-free-multipurpose-html-bootstrap-template/
 * Author: BootstrapMade.com
 * License: https://bootstrapmade.com/license/
 */

import 'bootstrap';
// import Swiper from 'swiper';
import GLightbox from 'glightbox';

// @ts-ignore — isotope-layout не имеет типов
import Isotope from 'isotope-layout';

document.addEventListener('DOMContentLoaded', (): void => {
  "use strict";

  /**
   * Easy selector helper function
   */
  const select = (el: string): HTMLElement | null => {
    return document.querySelector<HTMLElement>(el.trim());
  };

  const selectAll = (el: string): HTMLElement[] => {
    return [...document.querySelectorAll<HTMLElement>(el.trim())];
  };

  /**
   * Easy event listener function
   */
  const on = (type: string, el: string, listener: (this: HTMLElement, e: Event) => void, all: boolean = false): void => {
    if (all) {
      selectAll(el).forEach(e => e.addEventListener(type, listener));
    } else {
      select(el)?.addEventListener(type, listener);
    }
  };

  /**
   * Easy on scroll event listener
   */
  const onscroll = (el: Document | HTMLElement, listener: EventListener): void => {
    el.addEventListener('scroll', listener);
  };

  /**
   * Scroll Reveal — замена AOS через IntersectionObserver.
   *
   * Инициализируется первым, чтобы контент стал видимым
   * даже если последующие инициализации упадут с ошибкой.
   * CSS анимации определены в _gp-scroll-reveal.scss.
   * Элементы внутри .swiper игнорируются (защита от конфликта).
   */
  const aosElements = document.querySelectorAll<HTMLElement>('[data-aos]');
  if (aosElements.length > 0) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aos-animate');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    aosElements.forEach(el => {
      if (el.closest('.swiper')) {
        el.classList.add('aos-animate');
        return;
      }
      observer.observe(el);
    });
  }

  /**
   * Navbar links active state on scroll
   */
  const navbarlinks = selectAll('#navbar .scrollto');
  const navbarlinksActive = (): void => {
    const position = window.scrollY + 200;
    navbarlinks.forEach(navbarlink => {
      if (!(navbarlink as HTMLAnchorElement).hash) return;
      const section = select((navbarlink as HTMLAnchorElement).hash);
      if (!section) return;
      if (position >= section.offsetTop && position <= (section.offsetTop + section.offsetHeight)) {
        navbarlink.classList.add('active');
      } else {
        navbarlink.classList.remove('active');
      }
    });
  };
  window.addEventListener('load', navbarlinksActive);
  onscroll(document, navbarlinksActive);

  /**
   * Scrolls to an element with header offset
   */
  const scrollto = (el: string): void => {
    const header = select('#header');
    if (!header) return;
    const offset = header.offsetHeight;
    const target = select(el);
    if (!target) return;
    window.scrollTo({
      top: target.offsetTop - offset,
      behavior: 'smooth'
    });
  };

  /**
   * Toggle .header-scrolled class to #header when page is scrolled
   */
  const selectHeader = select('#header');
  if (selectHeader) {
    const headerScrolled = (): void => {
      if (window.scrollY > 100) {
        selectHeader.classList.add('header-scrolled');
      } else {
        selectHeader.classList.remove('header-scrolled');
      }
    };
    window.addEventListener('load', headerScrolled);
    onscroll(document, headerScrolled);
  }

  /**
   * Back to top button
   */
  const backtotop = select('.back-to-top');
  if (backtotop) {
    const toggleBacktotop = (): void => {
      if (window.scrollY > 100) {
        backtotop.classList.add('active');
      } else {
        backtotop.classList.remove('active');
      }
    };
    window.addEventListener('load', toggleBacktotop);
    onscroll(document, toggleBacktotop);
  }

  /**
   * Mobile nav toggle
   */
  on('click', '.mobile-nav-toggle', function (e) {
    const navbar = select('#navbar');
    const isOpen = navbar?.classList.toggle('navbar-mobile');
    document.body.classList.toggle('mobile-nav-active', isOpen);
    this.classList.toggle('bi-list');
    this.classList.toggle('bi-x');
  });

  /**
   * Mobile nav dropdowns activate
   */
  on('click', '.navbar .dropdown > a', function (e) {
    if (select('#navbar')?.classList.contains('navbar-mobile')) {
      e.preventDefault();
      (this.nextElementSibling as HTMLElement)?.classList.toggle('dropdown-active');
    }
  }, true);

  /**
   * Scroll with offset on links with a class name .scrollto
   */
  on('click', '.scrollto', function (e) {
    const hash = (this as HTMLAnchorElement).hash;
    if (select(hash)) {
      e.preventDefault();

      const navbar = select('#navbar');
      if (navbar?.classList.contains('navbar-mobile')) {
        navbar.classList.remove('navbar-mobile');
        document.body.classList.remove('mobile-nav-active');
        const navbarToggle = select('.mobile-nav-toggle');
        navbarToggle?.classList.toggle('bi-list');
        navbarToggle?.classList.toggle('bi-x');
      }
      scrollto(hash);
    }
  }, true);

  /**
   * Scroll with offset on page load with hash links in the url
   */
  window.addEventListener('load', () => {
    if (window.location.hash) {
      if (select(window.location.hash)) {
        scrollto(window.location.hash);
      }
    }
  });

  /**
   * Preloader
   */
  const preloader = select('#preloader');
  if (preloader) {
    window.addEventListener('load', () => {
      preloader.remove();
    });
  }

  /**
   * Clients Slider
   */
  const clientsSliderEl = select('.clients-slider');
  // if (clientsSliderEl) {
  //   new Swiper(clientsSliderEl as HTMLElement, {
  //     speed: 400,
  //     loop: true,
  //     autoplay: {
  //       delay: 5000,
  //       disableOnInteraction: false
  //     },
  //     slidesPerView: 'auto',
  //     pagination: {
  //       el: '.swiper-pagination',
  //       type: 'bullets',
  //       clickable: true
  //     },
  //     breakpoints: {
  //       320: {
  //         slidesPerView: 2,
  //         spaceBetween: 40
  //       },
  //       480: {
  //         slidesPerView: 3,
  //         spaceBetween: 60
  //       },
  //       640: {
  //         slidesPerView: 4,
  //         spaceBetween: 80
  //       },
  //       992: {
  //         slidesPerView: 6,
  //         spaceBetween: 120
  //       }
  //     }
  //   });
  // }

  /**
   * Portfolio isotope and filter
   */
  window.addEventListener('load', () => {
    const portfolioContainer = select('.portfolio-container');
    if (portfolioContainer) {
      const portfolioIsotope = new Isotope(portfolioContainer, {
        itemSelector: '.portfolio-item'
      });

      const portfolioFilters = selectAll('#portfolio-flters li');

      on('click', '#portfolio-flters li', function (e) {
        e.preventDefault();
        portfolioFilters.forEach((el) => {
          el.classList.remove('filter-active');
        });
        this.classList.add('filter-active');

        portfolioIsotope.arrange({
          filter: this.getAttribute('data-filter')
        });
      }, true);
    }
  });

  /**
   * Initiate portfolio lightbox
   */
  GLightbox({
    selector: '.portfolio-lightbox',
  });

  /**
   * Portfolio details slider
   */
  const portfolioSliderEl = select('.portfolio-details-slider');
  // if (portfolioSliderEl) {
  //   new Swiper(portfolioSliderEl as HTMLElement, {
  //     speed: 400,
  //     loop: true,
  //     autoplay: {
  //       delay: 5000,
  //       disableOnInteraction: false
  //     },
  //     pagination: {
  //       el: '.swiper-pagination',
  //       type: 'bullets',
  //       clickable: true
  //     }
  //   });
  // }

  /**
   * Description collapse — ограничение высоты описания актёра
   *
   * Desktop (lg+): высота ограничена высотой .portfolio-details-slider (фото).
   * Mobile (<lg):  высота ограничена 100vh.
   * Если текст короче лимита — кнопка и градиент не показываются.
   */
  const descriptionCollapse = select('.description-collapse');
  const descriptionContent = select('.description-collapse__content');
  const descriptionFade = select('.description-collapse__fade');
  const descriptionToggle = select('.description-collapse__toggle');

  if (descriptionCollapse && descriptionContent && descriptionFade && descriptionToggle) {
    const LG_BREAKPOINT = 992;
    let isExpanded = false;

    const getMaxHeight = (): number => {
      if (window.innerWidth >= LG_BREAKPOINT && portfolioSliderEl) {
        return portfolioSliderEl.offsetHeight;
      }
      return window.innerHeight;
    };

    const updateCollapse = (): void => {
      if (isExpanded) return;

      const limit = getMaxHeight();
      const naturalHeight = descriptionContent.scrollHeight;

      if (naturalHeight <= limit || limit <= 0) {
        descriptionCollapse.style.maxHeight = '';
        descriptionCollapse.style.overflow = '';
        descriptionFade.style.display = 'none';
        descriptionToggle.hidden = true;
      } else {
        descriptionCollapse.style.maxHeight = limit + 'px';
        descriptionCollapse.style.overflow = 'hidden';
        descriptionFade.style.display = '';
        descriptionToggle.hidden = false;
      }
    };

    descriptionToggle.addEventListener('click', (): void => {
      if (isExpanded) {
        isExpanded = false;
        descriptionCollapse.classList.remove('description-collapse--expanded');
        updateCollapse();
        descriptionToggle.textContent = 'Показать больше';
        setTimeout((): void => {
          descriptionToggle.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 300);
      } else {
        isExpanded = true;
        descriptionCollapse.style.maxHeight = descriptionContent.scrollHeight + 'px';
        descriptionCollapse.classList.add('description-collapse--expanded');
        descriptionToggle.textContent = 'Скрыть';
      }
    });

    if (portfolioSliderEl) {
      new ResizeObserver((): void => {
        if (!isExpanded) updateCollapse();
      }).observe(portfolioSliderEl);
    }

    window.addEventListener('resize', (): void => {
      if (!isExpanded) updateCollapse();
    });

    updateCollapse();
  }

  /**
   * Testimonials slider
   */
  const testimonialsSliderEl = select('.testimonials-slider');
  // if (testimonialsSliderEl) {
  //   new Swiper(testimonialsSliderEl as HTMLElement, {
  //     speed: 600,
  //     loop: true,
  //     autoplay: {
  //       delay: 5000,
  //       disableOnInteraction: false
  //     },
  //     slidesPerView: 'auto',
  //     pagination: {
  //       el: '.swiper-pagination',
  //       type: 'bullets',
  //       clickable: true
  //     }
  //   });
  // }

});

